import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import AdminSidebar from '../../components/sidebar/AdminSidebar';
import './VirtualAccountsPage.css';

interface VirtualAccount {
  _id: string;
  userId: string;
  userEmail: string;
  userName: string;
  accountNumber: string;
  accountName: string;
  bankName: string;
  reference: string;
  amount: number;
  status: 'INITIATED' | 'VIRTUAL_ACCOUNT_CREATED' | 'WAITING_FOR_TRANSFER' | 'COMPLETED' | 'FAILED';
  createdAt: string;
  updatedAt: string;
}

interface VirtualAccountsResponse {
  success: boolean;
  data: {
    virtualAccounts: VirtualAccount[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalRecords: number;
    };
  };
  error?: string;
}

const VirtualAccountsPage: React.FC = () => {
  const [virtualAccounts, setVirtualAccounts] = useState<VirtualAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupMessage, setCleanupMessage] = useState<string | null>(null);

  const fetchVirtualAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
        ...(statusFilter !== 'all' && { status: statusFilter })
      });

      const response: VirtualAccountsResponse = await adminApi.getVirtualAccounts(`?${params}`);
      if (response.success && response.data) {
        setVirtualAccounts(response.data.virtualAccounts);
        setTotalPages(response.data.pagination.totalPages);
      } else {
        setError(response.error || 'Failed to fetch virtual accounts');
      }
    } catch (err) {
      console.error('Failed to fetch virtual accounts:', err);
      setError('Failed to load virtual accounts');
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter]);

  useEffect(() => {
    fetchVirtualAccounts();
  }, [fetchVirtualAccounts]);

  const handleCleanup = async () => {
    const confirmCleanup = window.confirm(
      'This will mark stale WAITING_FOR_TRANSFER records as FAILED. Continue?'
    );
    if (!confirmCleanup) return;

    try {
      setCleanupLoading(true);
      setCleanupMessage(null);
      const response = await adminApi.cleanupVirtualAccounts();
      if (response.success) {
        setCleanupMessage(`Cleanup complete: ${response.data.modified} updated`);
        await fetchVirtualAccounts();
      } else {
        setCleanupMessage(response.message || 'Cleanup failed');
      }
    } catch (err) {
      console.error('Cleanup failed:', err);
      setCleanupMessage('Cleanup failed');
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleManualComplete = async (paymentId: string) => {
    const confirmComplete = window.confirm(
      'This will mark the payment as COMPLETED and credit the wallet. Continue?'
    );
    if (!confirmComplete) return;

    try {
      setCleanupLoading(true);
      setCleanupMessage(null);
      const response = await adminApi.completeVirtualAccount(paymentId);
      if (response.success) {
        setCleanupMessage('Payment completed and wallet credited');
        await fetchVirtualAccounts();
      } else {
        setCleanupMessage(response.message || 'Manual completion failed');
      }
    } catch (err) {
      console.error('Manual completion failed:', err);
      setCleanupMessage('Manual completion failed');
    } finally {
      setCleanupLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'INITIATED': return 'status-initiated';
      case 'VIRTUAL_ACCOUNT_CREATED': return 'status-created';
      case 'WAITING_FOR_TRANSFER': return 'status-waiting';
      case 'COMPLETED': return 'status-completed';
      case 'FAILED': return 'status-failed';
      default: return 'status-default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  const handleDownloadReceipt = async (paymentId: string) => {
    try {
      const blob = await adminApi.downloadFundingReceipt(paymentId);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/plain;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `funding-receipt-${paymentId}.txt`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download funding receipt:', err);
      setCleanupMessage('Failed to download receipt');
    }
  };

  const handleViewReceipt = async (paymentId: string) => {
    try {
      const response = await adminApi.getFundingReceipt(paymentId);
      if (!response.success) {
        setCleanupMessage('Failed to load receipt');
        return;
      }

      const lines = [
        'Kenluk Funding Receipt',
        '-----------------------',
        `Receipt ID: ${response.data.paymentId}`,
        `Transaction Ref: ${response.data.transactionRef || '-'}`,
        `Amount: ${formatCurrency(response.data.amount)}`,
        `Status: ${response.data.status}`,
        `Created At: ${new Date(response.data.createdAt).toLocaleString()}`,
        `Completed At: ${response.data.completedAt ? new Date(response.data.completedAt).toLocaleString() : '-'}`,
      ];
      window.alert(lines.join('\n'));
    } catch (err) {
      console.error('Failed to view funding receipt:', err);
      setCleanupMessage('Failed to load receipt');
    }
  };

  if (loading) {
    return (
      <DashboardLayout sidebar={<AdminSidebar />}>
        <div className="virtual-accounts-page">
          <div className="page-header">
            <h2>My Virtual Accounts</h2>
            <p>Manage virtual accounts for all users</p>
          </div>
          <div className="loading">Loading virtual accounts...</div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout sidebar={<AdminSidebar />}>
        <div className="virtual-accounts-page">
          <div className="page-header">
            <h2>My Virtual Accounts</h2>
            <p>Manage virtual accounts for all users</p>
          </div>
          <div className="error">{error}</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebar={<AdminSidebar />}>
      <div className="virtual-accounts-page">
        <div className="page-header">
          <h2>My Virtual Accounts</h2>
          <p>Manage virtual accounts for all users</p>
        </div>

        <div className="filters-section">
          <div className="filter-group">
            <label htmlFor="status-filter">Filter by Status:</label>
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="INITIATED">Initiated</option>
              <option value="VIRTUAL_ACCOUNT_CREATED">Account Created</option>
              <option value="WAITING_FOR_TRANSFER">Waiting for Transfer</option>
              <option value="COMPLETED">Completed</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleCleanup}
            disabled={cleanupLoading}
          >
            {cleanupLoading ? 'Cleaning...' : 'Cleanup Stale Accounts'}
          </button>
          {cleanupMessage && <div className="cleanup-message">{cleanupMessage}</div>}
        </div>

        <div className="accounts-table-container">
          <table className="accounts-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Account Details</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Reference</th>
                <th>Created</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {virtualAccounts.length > 0 ? (
                virtualAccounts.map((account) => (
                  <tr key={account._id}>
                    <td>
                      <div className="user-info">
                        <div className="user-name">{account.userName}</div>
                        <div className="user-email">{account.userEmail}</div>
                      </div>
                    </td>
                    <td>
                      <div className="account-details">
                        <div className="bank-name">{account.bankName}</div>
                        <div className="account-number">{account.accountNumber}</div>
                        <div className="account-name">{account.accountName}</div>
                      </div>
                    </td>
                    <td className="amount-cell">
                      {formatCurrency(account.amount)}
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusColor(account.status)}`}>
                        {account.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="reference-cell">
                      <code>{account.reference}</code>
                    </td>
                    <td className="date-cell">
                      {formatDate(account.createdAt)}
                    </td>
                    <td className="date-cell">
                      {formatDate(account.updatedAt)}
                    </td>
                    <td className="actions-cell">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => handleViewReceipt(account._id)}
                      >
                        View Receipt
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => handleDownloadReceipt(account._id)}
                      >
                        Download Receipt
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => handleManualComplete(account._id)}
                        disabled={cleanupLoading || account.status !== 'WAITING_FOR_TRANSFER'}
                      >
                        Complete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="no-data">
                    No virtual accounts found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="accounts-mobile-list">
          {virtualAccounts.length > 0 ? (
            virtualAccounts.map((account) => (
              <article key={`mobile-${account._id}`} className="account-mobile-card">
                <div className="account-mobile-head">
                  <div>
                    <div className="user-name">{account.userName}</div>
                    <div className="user-email">{account.userEmail}</div>
                  </div>
                  <span className={`status-badge ${getStatusColor(account.status)}`}>
                    {account.status.replace(/_/g, ' ')}
                  </span>
                </div>

                <div className="account-mobile-grid">
                  <div><strong>Bank:</strong> {account.bankName}</div>
                  <div><strong>Acct No:</strong> {account.accountNumber}</div>
                  <div><strong>Acct Name:</strong> {account.accountName}</div>
                  <div><strong>Amount:</strong> {formatCurrency(account.amount)}</div>
                  <div><strong>Reference:</strong> <code>{account.reference}</code></div>
                  <div><strong>Created:</strong> {formatDate(account.createdAt)}</div>
                  <div><strong>Updated:</strong> {formatDate(account.updatedAt)}</div>
                </div>

                <div className="account-mobile-actions">
                  <button type="button" className="btn-secondary" onClick={() => handleViewReceipt(account._id)}>
                    View Receipt
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => handleDownloadReceipt(account._id)}>
                    Download Receipt
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => handleManualComplete(account._id)}
                    disabled={cleanupLoading || account.status !== 'WAITING_FOR_TRANSFER'}
                  >
                    Complete
                  </button>
                </div>
              </article>
            ))
          ) : (
            <div className="no-data">No virtual accounts found</div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        )}

        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-label">Total Accounts:</span>
            <span className="stat-value">{virtualAccounts.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Active (Waiting):</span>
            <span className="stat-value">
              {virtualAccounts.filter(acc => acc.status === 'WAITING_FOR_TRANSFER').length}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Completed:</span>
            <span className="stat-value">
              {virtualAccounts.filter(acc => acc.status === 'COMPLETED').length}
            </span>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default VirtualAccountsPage;
